import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  IoAddCircleOutline, IoTrashOutline, IoCreateOutline, 
  IoRefreshOutline, IoPricetagOutline, IoCalendarOutline 
} from 'react-icons/io5';
import adminService from '../../services/adminService';
import styles from './AdminPromotionPage.module.scss'; // Import CSS Module

const PromotionPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // State form
  const [formData, setFormData] = useState({
    id: '',
    code: '',
    description: '',
    discount_type: 'percent',
    discount_value: '',
    start_date: '',
    end_date: ''
  });

  // Load danh sách
  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setIsLoading(true);
    try {
      const res = await adminService.getCoupons();
      setCoupons(res.data);
    } catch (error) {
      console.error("Lỗi tải dữ liệu", error);
      toast.error("Không thể tải danh sách mã.");
    } finally {
      setIsLoading(false);
    }
  };

  // Sinh mã tự động
  const generateRandomCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, code: 'SALE' + result }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(formData.discount_value) <= 0) {
      toast.warning("Giá trị giảm phải lớn hơn 0");
      return;
    }

    try {
      if (isEditing) {
        await adminService.updateCoupon(formData);
        toast.success('Đã cập nhật mã giảm giá!');
      } else {
        await adminService.createCoupon(formData);
        toast.success('Đã tạo mã giảm giá mới!');
      }
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi xảy ra!');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bạn chắc chắn muốn xóa mã này?')) {
      try {
        await adminService.deleteCoupon(id);
        toast.success('Đã xóa mã.');
        setCoupons(prev => prev.filter(c => c.id !== id));
      } catch (error) {
        toast.error('Lỗi khi xóa.');
      }
    }
  };

  const handleEdit = (coupon) => {
    setIsEditing(true);
    const formatDateTime = (dateStr) => {
        if(!dateStr) return '';
        return dateStr.replace(' ', 'T').substring(0, 16);
    };
    setFormData({
        ...coupon,
        start_date: formatDateTime(coupon.start_date),
        end_date: formatDateTime(coupon.end_date)
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setIsEditing(false);
    setFormData({ id: '', code: '', description: '', discount_type: 'percent', discount_value: '', start_date: '', end_date: '' });
  };

  const formatDiscountDisplay = (type, value) => {
      if(type === 'percent') return `${parseInt(value)}%`;
      return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
  };

  return (
    <div className={styles.promotionPage}>
      <div className={styles.pageHeader}>
        <h2><IoPricetagOutline /> Quản Lý Mã Khuyến Mãi</h2>
      </div>

      {/* --- FORM CARD --- */}
      <div className={styles.promoFormCard}>
        <div className={styles.cardHeader}>
            <h3>{isEditing ? 'Cập Nhật Mã' : 'Tạo Mã Mới'}</h3>
            {isEditing && <button className={styles.btnReset} onClick={resetForm}>Hủy bỏ</button>}
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            
            {/* Mã Code */}
            <div className={styles.formGroup}>
                <label>Mã Giảm Giá (Code) <span className={styles.required}>*</span></label>
                <div className={styles.inputWithBtn}>
                    <input 
                        type="text" name="code" 
                        value={formData.code} onChange={handleChange} 
                        placeholder="VD: SUMMER2025" required 
                        className={styles.codeInput}
                    />
                    <button type="button" onClick={generateRandomCode} className={styles.btnAuto} title="Sinh mã tự động">
                        <IoRefreshOutline /> Auto
                    </button>
                </div>
            </div>

            {/* Giá trị giảm */}
            <div className={styles.formGroup}>
                <label>Giá trị giảm <span className={styles.required}>*</span></label>
                <div className={styles.discountInputGroup}>
                    <input 
                        type="number" name="discount_value" 
                        value={formData.discount_value} onChange={handleChange} 
                        required placeholder="VD: 10" min="1"
                    />
                    <select name="discount_type" value={formData.discount_type} onChange={handleChange}>
                        <option value="percent">Phần trăm (%)</option>
                        <option value="fixed">Tiền mặt (VNĐ)</option>
                    </select>
                </div>
            </div>

            {/* Ngày tháng */}
            <div className={styles.formGroup}>
                <label>Ngày Bắt Đầu</label>
                <div className={styles.inputIconWrapper}>
                    <IoCalendarOutline className={styles.inputIcon} />
                    <input type="datetime-local" name="start_date" value={formData.start_date} onChange={handleChange} />
                </div>
            </div>

            <div className={styles.formGroup}>
                <label>Ngày Kết Thúc</label>
                <div className={styles.inputIconWrapper}>
                    <IoCalendarOutline className={styles.inputIcon} />
                    <input type="datetime-local" name="end_date" value={formData.end_date} onChange={handleChange} />
                </div>
            </div>

            {/* Mô tả */}
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                <label>Mô tả chương trình</label>
                <textarea 
                    name="description" 
                    value={formData.description} onChange={handleChange} 
                    placeholder="VD: Giảm giá nhân dịp khai trương..."
                ></textarea>
            </div>
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={`${styles.btnSubmit} ${isEditing ? styles.btnUpdate : styles.btnCreate}`}>
                {isEditing ? <><IoCreateOutline /> Lưu Thay Đổi</> : <><IoAddCircleOutline /> Tạo Mã Ngay</>}
            </button>
          </div>
        </form>
      </div>

      {/* --- TABLE DANH SÁCH --- */}
      <div className={styles.tableContainer}>
        <table>
            <thead>
                <tr>
                    <th>STT</th>
                    <th>Mã Code</th>
                    <th>Giá Trị</th>
                    <th>Thời Gian Áp Dụng</th>
                    <th>Mô Tả</th>
                    <th>Trạng Thái</th>
                    <th className="text-center">Hành Động</th>
                </tr>
            </thead>
            <tbody>
                {isLoading ? (
                    <tr><td colSpan="7" className="text-center">Đang tải dữ liệu...</td></tr>
                ) : coupons.length > 0 ? (
                    coupons.map((coupon, index) => {
                        const isExpired = coupon.end_date && new Date(coupon.end_date) < new Date();
                        return (
                            <tr key={coupon.id} className={isExpired ? styles.rowExpired : ''}>
                                <td>{index + 1}</td>
                                <td><span className={styles.codeBadge}>{coupon.code}</span></td>
                                <td>
                                    <span className={`${styles.valueBadge} ${styles[coupon.discount_type]}`}>
                                        -{formatDiscountDisplay(coupon.discount_type, coupon.discount_value)}
                                    </span>
                                </td>
                                <td className={styles.dateCell}>
                                    <small>Từ: {coupon.start_date ? new Date(coupon.start_date).toLocaleDateString('vi-VN') : '---'}</small>
                                    <small>Đến: {coupon.end_date ? new Date(coupon.end_date).toLocaleDateString('vi-VN') : 'Vô thời hạn'}</small>
                                </td>
                                <td className={styles.descCell}>{coupon.description}</td>
                                <td>
                                    {isExpired ? 
                                        <span className={`${styles.statusBadge} ${styles.expired}`}>Đã hết hạn</span> : 
                                        <span className={`${styles.statusBadge} ${styles.active}`}>Đang chạy</span>
                                    }
                                </td>
                                <td className="text-center">
                                    <div className={styles.actionButtons}>
                                        <button onClick={() => handleEdit(coupon)} className={`${styles.btnIcon} ${styles.edit}`} title="Sửa"><IoCreateOutline /></button>
                                        <button onClick={() => handleDelete(coupon.id)} className={`${styles.btnIcon} ${styles.delete}`} title="Xóa"><IoTrashOutline /></button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr><td colSpan="7" className={styles.emptyState}>Chưa có mã khuyến mãi nào.</td></tr>
                )}
            </tbody>
        </table>
      </div>
    </div>
  );
};

export default PromotionPage;